import React, { useState } from "react";
import { Button, Card, Flex, ModalV2, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import {
  useDeletePaymentAccountMutation,
  useRemoveProfileFromCaseMutation,
} from "~/graphql/supergraphHooks";
import { getProfileName } from "../../Profiles/utils";
import { BankAccountDomicile } from "../BankAccountDomicile";
import { usePaymentInstructions } from "../PaymentInstructionsContext";
import { BankDetailsProfileType, PaymentAccountDetailsType } from "../types";
import { findBeneficiaryProfile } from "../utils";
import {
  labelField,
  container,
  rightBox,
  rightTopContainer,
  trashIcon,
  bankIconBox,
  valueField,
  trashIconDisabled,
  modalContent,
  modalActions,
  modalButton,
} from "./styles_vanilla.css";

type Props = {
  caseId: number;
  paymentAccount: PaymentAccountDetailsType;
  readonly: boolean;
};

const ManualInstruction = ({ caseId, paymentAccount, readonly }: Props) => {
  const { investor, profiles } = usePaymentInstructions();
  const paymentBeneficiaryProfile = paymentAccount.profile;
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const bankName = paymentAccount?.providerName;
  const accountHolder =
    paymentAccount?.payeeName ?? getProfileName(paymentBeneficiaryProfile) ?? "--";
  const country = paymentAccount?.bankCountry?.name;

  const shouldDeleteProfile = (profile: BankDetailsProfileType | null | undefined): boolean => {
    if (!profile) {
      return false;
    }

    if (profile.id === investor?.id) {
      return false;
    }

    const profileInProfiles = profiles.find((p) => p?.id === profile.id);
    if (!profileInProfiles) {
      return false;
    }

    return findBeneficiaryProfile(profileInProfiles.parentRelationships);
  };

  const [deletePaymentAccountMutation] = useDeletePaymentAccountMutation({
    refetchQueries: ["GetInvestorBankDetails", "GetCaseInvestingProfileRiskRating"],
    awaitRefetchQueries: true,
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const [removeProfileFromCaseMutation] = useRemoveProfileFromCaseMutation({
    refetchQueries: ["GetInvestorBankDetails", "GetCaseInvestingProfileRiskRating"],
    awaitRefetchQueries: true,
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  const handleDeleteClick = () => {
    if (shouldDeleteProfile(paymentBeneficiaryProfile)) {
      setShowDeleteModal(true);
    } else {
      void confirmDelete();
    }
  };

  const confirmDelete = async () => {
    try {
      await deletePaymentAccountMutation({
        variables: { caseId: caseId, paymentAccountId: paymentAccount?.id ?? 0 },
      });

      if (shouldDeleteProfile(paymentBeneficiaryProfile)) {
        await removeProfileFromCaseMutation({
          variables: {
            caseId: caseId,
            profileId: paymentBeneficiaryProfile?.id ?? 0,
          },
        });
      }

      toast.success("Bank account deleted");
      setShowDeleteModal(false);
    } catch (err: unknown) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("An unknown error occurred");
      }
      setShowDeleteModal(false);
    }
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <Card className={container}>
        <Flex className={bankIconBox}>
          <Icon type="university" />
        </Flex>
        <Flex flexDirection="column" className={rightBox}>
          <Flex justifyContent="space-between" className={rightTopContainer}>
            <Text variant="strong-lg">{bankName || ""}</Text>
            <Icon
              type="trash"
              data-testid="trash-icon"
              className={readonly ? trashIconDisabled : trashIcon}
              color={readonly ? Colors.Neutral400 : Colors.Neutral700}
              onClick={readonly ? undefined : handleDeleteClick}
            />
          </Flex>
          <Flex gap={Sizes.XL}>
            <Flex flexDirection="column">
              <Text variant="strong-md" className={labelField}>
                Account holder name
              </Text>
              <Text variant="strong-md" className={labelField}>
                Bank name
              </Text>
              <Text variant="strong-md" className={labelField}>
                Bank account domicile
              </Text>
            </Flex>
            <Flex flexDirection="column">
              <Text variant="strong-md" className={valueField}>
                {accountHolder}
              </Text>
              <Text variant="strong-md" className={valueField}>
                {bankName}
              </Text>
              <BankAccountDomicile countryName={country} />
            </Flex>
          </Flex>
        </Flex>
      </Card>
      <ModalV2
        show={showDeleteModal}
        onHide={closeModal}
        isCentered={true}
        backdrop={true}
        title="Delete payment instruction"
      >
        <Flex flexDirection="column" className={modalContent}>
          <Text>
            Deleting this payment instruction will also delete the associated profile. Are you
            sure you want to continue?
          </Text>
        </Flex>
        <Flex justifyContent="flex-end" className={modalActions}>
          <Button onClick={closeModal} variant="neutral" className={modalButton}>
            Cancel
          </Button>
          <Button onClick={() => void confirmDelete()} variant="danger" className={modalButton}>
            Delete
          </Button>
        </Flex>
      </ModalV2>
    </>
  );
};

export default ManualInstruction;
